import React, { useState, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import GreenButton from '../../components/GreenButton';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

import {
  Button,
  Grid,
  Paper,
  List,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import WeatherWallEditDialog from './WeatherWallEditDialog';
import { WallFolder } from 'types/models/WallFolder';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FolderEditDialog from 'features/wallFolders/FolderEditDialog';
import FolderListItem from 'features/wallFolders/FolderListItem';
import WallListItem from 'features/wallFolders/WallListItem';
import WallsWithoutFoldersContainer from 'features/wallFolders/WallsWithoutFoldersContainer';
import { useDeleteWeatherWall } from './api/deleteWeatherWall';
import { useDeleteWeatherFolder } from 'features/wallFolders/api/deleteWeatherFolder';
import { WeatherWall } from 'types';
import { useWeatherFolders } from 'features/wallFolders/api/getWeatherFolders';

const ManageWeatherWallsPage = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { data: folders } = useWeatherFolders({ userId: user?.id });
  const deleteWeatherWall = useDeleteWeatherWall({});
  const deleteWeatherFolder = useDeleteWeatherFolder({});

  const [selectedWall, setSelectedWall] = useState<WeatherWall | undefined>(
    undefined
  );
  const [selectedFolder, setSelectedFolder] = useState<WallFolder | undefined>(
    undefined
  );
  const [foldersOpenStatus, setFoldersOpenStatus] = useState<{
    [id: number]: boolean;
  }>({});

  const [wallEditDialogOpen, setWallEditDialogOpen] = useState(false);
  const [folderEditDialogOpen, setFolderEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleCreateWallClicked = async () => {
    setSelectedWall(undefined);
    setWallEditDialogOpen(!wallEditDialogOpen);
  };

  const handleCreateFolderClicked = async () => {
    setFolderEditDialogOpen(true);
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClicked = async () => {
    if (selectedWall) {
      await deleteWeatherWall.mutateAsync(selectedWall.id);
      setSelectedWall(undefined);
    } else if (selectedFolder) {
      await deleteWeatherFolder.mutateAsync(selectedFolder.id);
    }
  };

  const handleWallClicked = (wall: WeatherWall) => {
    if (selectedWall?.id === wall.id) setSelectedWall(undefined);
    else setSelectedWall(wall);
    setSelectedFolder(undefined);
  };

  const handleFolderClicked = (folder: WallFolder) => {
    if (folder.id === selectedFolder?.id) setSelectedFolder(undefined);
    else setSelectedFolder(folder);
    setSelectedWall(undefined);
    const currentFolderStatus = foldersOpenStatus[folder.id];
    const newFolderStatusState = { ...foldersOpenStatus };
    newFolderStatusState[folder.id] = !currentFolderStatus;
    setFoldersOpenStatus(newFolderStatusState);
  };

  const handleEditClicked = () => {
    if (selectedWall) {
      navigate(`/edit-weather-wall/${selectedWall.id}`);
    } else if (selectedFolder) {
      setFolderEditDialogOpen(true);
    }
  };

  const handleEditDialogClosed = async () => {
    setWallEditDialogOpen(false);
  };

  const handleFolderDialogClosed = async () => {
    setFolderEditDialogOpen(false);
  };

  const [newAnchorEl, setNewAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const newOpen = Boolean(newAnchorEl);
  const handleNewClicked = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setNewAnchorEl(event.currentTarget);
    },
    []
  );
  const handleNewClose = () => {
    setNewAnchorEl(null);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={
          selectedWall
            ? `Weather Wall ${selectedWall?.name}`
            : `Folder ${selectedFolder?.name}`
        }
        onSubmit={handleDeleteClicked}
        onClose={() => setDeleteDialogOpen(false)}
      />
      <WeatherWallEditDialog
        open={wallEditDialogOpen}
        handleClose={handleEditDialogClosed}
        selectedWall={selectedWall}
      />
      <FolderEditDialog
        open={folderEditDialogOpen}
        handleClose={handleFolderDialogClosed}
        selectedFolder={selectedFolder}
        weather
      />
      <Grid container spacing={2}>
        <Grid container item xs={12} spacing={1}>
          <Grid item>
            <GreenButton name='New' onClick={handleNewClicked}></GreenButton>
            <Menu
              id='new-menu'
              anchorEl={newAnchorEl}
              open={newOpen}
              onClose={handleNewClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem
                onClick={() => {
                  handleCreateWallClicked();
                  handleNewClose();
                }}
              >
                Wall
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCreateFolderClicked();
                  handleNewClose();
                }}
              >
                Folder
              </MenuItem>
            </Menu>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              onClick={handleEditClicked}
              disabled={
                !!(
                  (!selectedWall && !selectedFolder) ||
                  (selectedWall && selectedFolder)
                )
              }
            >
              Edit
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='error'
              onClick={handleDeleteDialogOpen}
              disabled={
                !!(
                  (!selectedWall && !selectedFolder) ||
                  (selectedWall && selectedFolder)
                )
              }
            >
              Delete
            </Button>
          </Grid>
        </Grid>
        <Grid item container lg={6} xs={8}>
          <Grid item component={Paper} xs>
            <List sx={{ overflow: 'auto', maxHeight: '80vh' }}>
              {folders?.map((folder) => (
                <Fragment key={folder.id + 'fragment'}>
                  <FolderListItem
                    folder={folder}
                    selected={folder.id === selectedFolder?.id}
                    handleFolderClicked={handleFolderClicked}
                    foldersOpenStatus={foldersOpenStatus}
                  />
                  <Collapse
                    in={foldersOpenStatus[folder.id]}
                    timeout='auto'
                    unmountOnExit
                  >
                    {folder.weatherWalls.map((wall) => (
                      <WallListItem
                        wall={wall}
                        selected={selectedWall?.id === wall.id}
                        handleWallClicked={handleWallClicked}
                        key={wall.id + 'wall'}
                        folder={folder}
                      />
                    ))}
                  </Collapse>
                </Fragment>
              ))}
              <WallsWithoutFoldersContainer
                handleWallClicked={handleWallClicked}
                selectedWallId={selectedWall?.id}
                weather
              />
            </List>
          </Grid>
        </Grid>
      </Grid>
    </DndProvider>
  );
};

export default ManageWeatherWallsPage;
